
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { 
  Download, 
  RefreshCw, 
  Globe, 
  FileText, 
  CheckCircle, 
  AlertCircle,
  ExternalLink,
  Calendar,
  Hash,
  Copy
} from "lucide-react";
import { SitemapService } from "@/services/sitemapService";
import { downloadSitemap, validateSitemapXML } from "@/utils/sitemapGenerator";
import { config } from "@/config/environment";

interface SitemapStats {
  totalUrls: number;
  staticUrls: number;
  dynamicUrls: number;
  lastGenerated?: string;
}

export const SitemapManager = () => {
  const [sitemapContent, setSitemapContent] = useState<string>("");
  const [stats, setStats] = useState<SitemapStats | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [validationResult, setValidationResult] = useState<{
    isValid: boolean;
    errors: string[];
  } | null>(null);

  const sitemapService = new SitemapService();

  /**
   * Generate sitemap and update state
   */
  const handleGenerateSitemap = async () => {
    setIsGenerating(true);
    try {
      const [xmlContent, sitemapStats] = await Promise.all([
        sitemapService.generateSitemap(),
        sitemapService.getSitemapStats()
      ]);
      
      setSitemapContent(xmlContent);
      setStats(sitemapStats);
      
      // Validate the generated sitemap
      const validation = validateSitemapXML(xmlContent);
      setValidationResult(validation);
      
      toast.success("Sitemap generated successfully!");
    } catch (error) {
      console.error('Error generating sitemap:', error);
      toast.error("Failed to generate sitemap. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * Download sitemap file
   */
  const handleDownloadSitemap = async () => {
    if (!sitemapContent) {
      toast.error("Please generate sitemap first");
      return;
    }

    setIsDownloading(true);
    try {
      await downloadSitemap();
      toast.success("Sitemap downloaded successfully!");
    } catch (error) {
      console.error('Error downloading sitemap:', error);
      toast.error("Failed to download sitemap. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  /**
   * Copy sitemap URL to clipboard
   */
  const handleCopySitemapUrl = () => {
    const sitemapUrl = `${config.appUrl}/sitemap.xml`;
    navigator.clipboard.writeText(sitemapUrl);
    toast.success("Sitemap URL copied to clipboard!");
  };

  /**
   * Copy sitemap content to clipboard for manual update
   */
  const handleCopyXMLContent = () => {
    if (!sitemapContent) {
      toast.error("Please generate sitemap first");
      return;
    }
    
    navigator.clipboard.writeText(sitemapContent);
    toast.success("Sitemap XML copied to clipboard! You can now manually update the public/sitemap.xml file.");
  };

  // Auto-generate sitemap on component mount
  useEffect(() => {
    handleGenerateSitemap();
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-teedee-blue mb-2">
          XML Sitemap Management
        </h2>
        <p className="text-gray-600">
          Generate and manage your website's XML sitemap for search engines
        </p>
      </div>

      {/* Important Notice */}
      <Alert className="border-blue-200 bg-blue-50">
        <AlertCircle className="h-4 w-4 text-blue-600" />
        <AlertDescription>
          <div className="text-blue-700">
            <div className="font-medium mb-2">Important: Static Sitemap Setup</div>
            <div className="text-sm space-y-1">
              <div>• The sitemap is served from <code>/public/sitemap.xml</code> for proper SEO</div>
              <div>• Generate the sitemap here, then copy the XML content to update the static file</div>
              <div>• This ensures search engines receive proper XML content instead of HTML</div>
            </div>
          </div>
        </AlertDescription>
      </Alert>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <Button
          onClick={handleGenerateSitemap}
          disabled={isGenerating}
          className="bg-teedee-blue hover:bg-teedee-blue/90"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isGenerating ? 'animate-spin' : ''}`} />
          {isGenerating ? 'Generating...' : 'Generate Sitemap'}
        </Button>
        
        <Button
          onClick={handleCopyXMLContent}
          disabled={!sitemapContent}
          variant="outline"
        >
          <Copy className="h-4 w-4 mr-2" />
          Copy XML Content
        </Button>
        
        <Button
          onClick={handleDownloadSitemap}
          disabled={!sitemapContent || isDownloading}
          variant="outline"
        >
          <Download className="h-4 w-4 mr-2" />
          {isDownloading ? 'Downloading...' : 'Download XML'}
        </Button>
        
        <Button
          onClick={handleCopySitemapUrl}
          variant="outline"
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          Copy Sitemap URL
        </Button>
      </div>

      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center">
                <Hash className="h-4 w-4 mr-2 text-teedee-blue" />
                Total URLs
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-2xl font-bold text-teedee-blue">
                {stats.totalUrls}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center">
                <FileText className="h-4 w-4 mr-2 text-bobo-green" />
                Static URLs
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-2xl font-bold text-bobo-green">
                {stats.staticUrls}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center">
                <Globe className="h-4 w-4 mr-2 text-golden-orange" />
                Dynamic URLs
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-2xl font-bold text-golden-orange">
                {stats.dynamicUrls}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Validation Status */}
      {validationResult && (
        <Alert className={validationResult.isValid ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}>
          {validationResult.isValid ? (
            <CheckCircle className="h-4 w-4 text-green-600" />
          ) : (
            <AlertCircle className="h-4 w-4 text-red-600" />
          )}
          <AlertDescription>
            {validationResult.isValid ? (
              <span className="text-green-700">
                Sitemap validation passed - XML structure is valid
              </span>
            ) : (
              <div className="text-red-700">
                <div className="font-medium mb-1">Sitemap validation failed:</div>
                <ul className="list-disc list-inside space-y-1">
                  {validationResult.errors.map((error, index) => (
                    <li key={index} className="text-sm">{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Sitemap Preview */}
      {sitemapContent && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Sitemap Preview
            </CardTitle>
            <CardDescription>
              Generated XML sitemap content (first 50 lines)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-50 border rounded-lg p-4 max-h-96 overflow-y-auto">
              <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                {sitemapContent.split('\n').slice(0, 50).join('\n')}
                {sitemapContent.split('\n').length > 50 && '\n... (truncated)'}
              </pre>
            </div>
          </CardContent>
        </Card>
      )}

      {/* SEO Information */}
      <Card>
        <CardHeader>
          <CardTitle>SEO Information</CardTitle>
          <CardDescription>
            Important information about sitemap usage
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-medium mb-2">Sitemap URL:</h4>
            <code className="bg-gray-100 px-2 py-1 rounded text-sm">
              {config.appUrl}/sitemap.xml
            </code>
          </div>
          
          <Separator />
          
          <div>
            <h4 className="font-medium mb-2 text-orange-600">Manual Update Required:</h4>
            <div className="space-y-2 text-sm text-gray-600">
              <div>1. Click "Generate Sitemap" to create updated content</div>
              <div>2. Click "Copy XML Content" to copy the generated sitemap</div>
              <div>3. Manually update the <code>/public/sitemap.xml</code> file with the new content</div>
              <div>4. Deploy your changes to make the updated sitemap live</div>
            </div>
          </div>
          
          <Separator />
          
          <div>
            <h4 className="font-medium mb-2">Search Engine Submission:</h4>
            <div className="space-y-2 text-sm text-gray-600">
              <div>• Submit to Google Search Console</div>
              <div>• Submit to Bing Webmaster Tools</div>
              <div>• Include in robots.txt file (already done)</div>
              <div>• Update sitemap when content changes</div>
            </div>
          </div>
          
          <Separator />
          
          <div>
            <h4 className="font-medium mb-2">Included Content:</h4>
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">Static Pages</Badge>
              <Badge variant="secondary">Character Pages</Badge>
              <Badge variant="secondary">Legal Pages</Badge>
              <Badge variant="outline">Episodes (Future)</Badge>
              <Badge variant="outline">Activities (Future)</Badge>
            </div>
          </div>
          
          {stats?.lastGenerated && (
            <>
              <Separator />
              <div className="flex items-center text-sm text-gray-600">
                <Calendar className="h-4 w-4 mr-2" />
                Last generated: {new Date(stats.lastGenerated).toLocaleString()}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
